import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, Shield, Heart, Flame, Phone, Globe } from 'lucide-react';
import BottomNavigation from '../components/BottomNavigation';
import LanguageToggle from '../components/LanguageToggle';
import { EMERGENCY_NUMBERS } from '../data/emergencyNumbers';

const ICONS = { AlertTriangle, Shield, Heart, Flame, Phone, Globe };

const CATEGORY_STYLES = {
  primary:   { badge: 'bg-red-100 text-red-700 ring-red-200',     icon: 'text-red-600',    ring: 'ring-red-100' },
  police:    { badge: 'bg-blue-100 text-blue-700 ring-blue-200',  icon: 'text-blue-600',   ring: 'ring-blue-100' },
  medical:   { badge: 'bg-rose-100 text-rose-700 ring-rose-200',  icon: 'text-rose-600',   ring: 'ring-rose-100' },
  fire:      { badge: 'bg-orange-100 text-orange-700 ring-orange-200', icon: 'text-orange-600', ring: 'ring-orange-100' },
  support:   { badge: 'bg-purple-100 text-purple-700 ring-purple-200', icon: 'text-purple-600', ring: 'ring-purple-100' },
  consulate: { badge: 'bg-gray-100 text-gray-700 ring-gray-200',   icon: 'text-gray-600',   ring: 'ring-gray-100' },
};

const Emergency = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="emergency-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-white/20" data-testid="emergency-back-btn">
              <ArrowLeft className="h-5 w-5 rtl-flip" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" /> {t('emergency.title', 'Emergency Numbers')}
              </h1>
              <p className="text-xs text-red-100">{t('emergency.subtitle', 'Almería region — tap any number to call')}</p>
            </div>
          </div>
          <LanguageToggle />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6" data-testid="emergency-disclaimer">
          <p className="text-sm text-amber-900">
            <strong>{t('emergency.disclaimerTitle', 'In a life-threatening emergency:')}</strong>{' '}
            <a href="tel:112" className="font-bold underline">{t('emergency.callNow', 'call 112 now')}</a>.{' '}
            {t('emergency.disclaimerBody', 'This page is informational and may not reflect the latest official contact changes.')}
          </p>
        </div>

        <div className="space-y-3">
          {EMERGENCY_NUMBERS.map((e) => {
            const Icon = ICONS[e.icon] || Phone;
            const s = CATEGORY_STYLES[e.category] || CATEGORY_STYLES.consulate;
            return (
              <a
                key={e.id}
                href={`tel:${e.number}`}
                className={`flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm hover:shadow-md ring-1 ${s.ring} transition-shadow active:scale-[0.99]`}
                data-testid={`emergency-${e.id}`}
              >
                <div className={`w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center shrink-0 ${s.icon}`}>
                  <Icon className="h-6 w-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 truncate">{t(e.nameKey)}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ${s.badge}`}>
                      {t(`emergency.cat.${e.category}`)}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{t(e.descKey)}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono font-bold text-gray-900 text-lg tabular-nums">{e.displayNumber || e.number}</p>
                  <p className="text-[11px] text-gray-400 uppercase tracking-wide">{t('emergency.tapToCall', 'Tap to call')}</p>
                </div>
              </a>
            );
          })}
        </div>

        <p className="text-xs text-gray-400 text-center mt-8">
          {t('emergency.footerNote', 'Emergency numbers last reviewed February 2026. Always verify critical information with official sources.')}
        </p>
      </main>

      <BottomNavigation />
    </div>
  );
};

export default Emergency;
